// src/contexts/DisplayPreferencesContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { StoreContext } from './StoreContext';
import { paymentV2StoreGateAllows } from '../utils/paymentV2StoreGate';

const DisplayPreferencesContext = createContext({
  showPrices: true,
  showContact: true,
  showFAQ: true,
  showCart: false,
  cartVisibilityLoading: true,
  showRelated: true,
  showReviews: true,
  isUnderMaintenance: false,
  maintenanceMessage: '',
  showCustomInstructions: true,
  showNewBadge: true,
  showUncategorized: false,
  loading: true,
});

export function DisplayPreferencesProvider({ children }) {
  const [state, setState] = useState(() => {
    try {
      const cached = localStorage.getItem('displayPreferences');
      if (cached) {
        const parsed = JSON.parse(cached);
        return {
          showPrices: parsed.showPrices !== false,
          showContact: parsed.showContact !== false,
          showFAQ: parsed.showFAQ !== false,
          // Cached display preferences are presentation-only. Never let an old
          // browser value expose cart entry points before both live documents
          // have re-established the complete payment-v2 contract.
          showCart: false,
          showRelated: parsed.showRelated !== false,
          showReviews: parsed.showReviews !== false,
          isUnderMaintenance: parsed.isUnderMaintenance === true,
          maintenanceMessage: parsed.maintenanceMessage || '',
          showCustomInstructions: parsed.showCustomInstructions !== false,
          showNewBadge: parsed.showNewBadge !== false,
          showUncategorized: parsed.showUncategorized === true,
          loading: true,
        };
      }
    } catch {}
    return { showPrices: true, showContact: true, showFAQ: true, showCart: false, showRelated: true, showReviews: true, isUnderMaintenance: false, maintenanceMessage: '', showCustomInstructions: true, showNewBadge: true, showUncategorized: false, loading: true };
  });
  const { store, loading: storeLoading } = useContext(StoreContext);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'website_display_preferences', 'default'),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const prefs = {
            showPrices: data.show_prices !== false,
            showContact: data.show_contact !== false,
            showFAQ: data.show_faq !== false,
            // Cart visibility is an explicit opt-in. A missing field must not
            // silently reopen a checkout surface.
            showCart: data.show_cart === true,
            showRelated: data.show_related !== false,
            showReviews: data.show_reviews !== false,
            isUnderMaintenance: data.is_under_maintenance === true,
            maintenanceMessage: data.maintenance_message || '',
            showCustomInstructions: data.show_custom_instructions !== false,
            showNewBadge: data.show_new_badge !== false,
            showUncategorized: data.show_uncategorized === true,
            loading: false,
          };
          setState(prefs);
          try {
            localStorage.setItem('displayPreferences', JSON.stringify(prefs));
          } catch {}
        } else {
          setState({ showPrices: true, showContact: true, showFAQ: true, showCart: false, showRelated: true, showReviews: true, isUnderMaintenance: false, maintenanceMessage: '', showCustomInstructions: true, showNewBadge: true, showUncategorized: false, loading: false });
        }
      },
      (error) => {
        console.error('Error fetching display preferences:', error);
        setState((prev) => ({ ...prev, showCart: false, loading: false }));
      }
    );

    return () => unsubscribe();
  }, []);

  const value = {
    ...state,
    cartVisibilityLoading: state.loading || storeLoading,
    // Catalog rendering does not depend on checkout readiness. Only the cart
    // surface is held fail-closed until both live listeners are ready and the
    // exact server-owned payment contract is enabled.
    showCart: Boolean(
      !state.loading &&
      !storeLoading &&
      state.showCart &&
      paymentV2StoreGateAllows(store)
    ),
  };

  return (
    <DisplayPreferencesContext.Provider value={value}>
      {children}
    </DisplayPreferencesContext.Provider>
  );
}

export function useDisplayPreferences() {
  return useContext(DisplayPreferencesContext);
}
